import { useEffect, useState } from "react";
import { Box, Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Globe, Plus, Unlink } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogActions,
} from "../../../components/Dialog";
import { AlertNote } from "../../../components/AlertNote";
import { Button } from "../../../components/Button";
import { ConfirmModal } from "../../../components/ConfirmModal";
import { CustomSelector } from "../../../components/CustomSelector";
import { linkSeRankingSite, unlinkSeRankingSite } from "../../../services/seRankingService";
import { useSeRankingAvailableSites } from "../seoSetup/hooks";
import { ConnectingClientField } from "./ConnectingClientField";
import { useAuth } from "../../../components/contexts/AuthContext";
import { SEO_PERMISSION_MESSAGE, getSeoProvider } from "@/utils/agencyFeatures";

const errorText = (err, fallback, seo) => {
  // A 502 wraps a raw SE Ranking error that is neither safe nor useful to show
  // verbatim, so it is always replaced with a friendly message.
  if (err?.response?.status === 502) {
    return `${seo.Source} no ha respondido. Vuelve a intentarlo en unos minutos y, si el problema persiste, contacta con soporte.`;
  }
  const detail = err?.response?.data?.detail;
  // Backend details name the provider
  if (typeof detail === "string" && seo.branded) return detail;
  return fallback;
};

/** "Clínica Dental (clinicadental.es)" — title first, domain as the qualifier. */
export const seRankingSiteLabel = (site) => {
  if (!site) return "";
  const title = site.site_title || site.site_domain || `Proyecto ${site.site_id}`;
  if (site.site_domain && site.site_title) {
    return `${title} (${site.site_domain})`;
  }
  return title;
};

/**
 * Link one SE Ranking project (site) to a client.
 *
 * Simpler than the Google dialog: SE Ranking is read with a single master key,
 * and a superadmin is the one who links each user (`agency_admin`/
 * `consultant`) to their sub-account, so there is no OAuth step here — only the
 * choice of which project of the current user's own sub-account this client is.
 */
export function ConnectSeRankingModal({
  open,
  onOpenChange,
  clientId,
  // { name, logo } shown above the selector so it is clear which client is being linked.
  client,
  // The client↔site link row as the list endpoint returns it, or null. Its `id`
  // is what unlinking is addressed with (never the client id), and its
  // `site_id` is what preselects the current project in the selector.
  currentLink,
  onConnected,
  onDisconnected,
  onCreateProject,
}) {
  const theme = useTheme();
  const { user } = useAuth();
  const seo = getSeoProvider(user);

  const sitesQuery = useSeRankingAvailableSites(clientId, { enabled: open });
  const sites = sitesQuery.data?.sites || [];
  const isLoadingSites = sitesQuery.isLoading;
  // Older backends answered 404/409 instead of `connected: false`.
  const legacyNotConnected = [404, 409].includes(sitesQuery.error?.response?.status);
  const userNotConnected = legacyNotConnected || (sitesQuery.data && !sitesQuery.data.connected);
  const [siteId, setSiteId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [actionError, setActionError] = useState("");
  const error =
    actionError ||
    (sitesQuery.error && !legacyNotConnected
      ? errorText(sitesQuery.error, `No se han podido cargar los proyectos de ${seo.label}.`, seo)
      : "");

  useEffect(() => {
    if (open) setActionError("");
  }, [open]);

  useEffect(() => {
    if (!open || !sitesQuery.data) return;
    const available = sitesQuery.data.sites;
    const preselected =
      available.find((s) => String(s.site_id) === String(currentLink?.site_id)) ||
      (available.length === 1 ? available[0] : null);
    setSiteId(preselected ? String(preselected.site_id) : "");
  }, [open, sitesQuery.data, currentLink?.site_id]);

  const handleConnect = async () => {
    const site = sites.find((s) => String(s.site_id) === String(siteId));
    if (!site) return;

    setIsSaving(true);
    setActionError("");
    try {
      // The POST answers with the link row itself, id included, so the panel
      // can start calling the metric endpoints without re-reading the list.
      const link = await linkSeRankingSite({
        client: clientId,
        // Needed when the list mixes several sub-accounts (a superadmin's).
        seranking_account: site.seranking_account,
        site_id: site.site_id,
        site_title: site.site_title,
        site_domain: site.site_domain,
      });
      onConnected?.(link);
      onOpenChange(false);
    } catch (err) {
      setActionError(errorText(err, "No se ha podido guardar la conexión.", seo));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!currentLink?.id) return;

    setIsUnlinking(true);
    setActionError("");
    try {
      await unlinkSeRankingSite(currentLink.id);
      setConfirmUnlink(false);
      onDisconnected?.();
      onOpenChange(false);
    } catch (err) {
      setConfirmUnlink(false);
      setActionError(errorText(err, "No se ha podido desvincular el proyecto.", seo));
    } finally {
      setIsUnlinking(false);
    }
  };

  // A superadmin gets projects from several sub-accounts: say whose each one is.
  const multipleAccounts =
    new Set(sites.map((site) => site.seranking_account)).size > 1;
  const siteOptions = sites.map((site) => ({
    value: String(site.site_id),
    label:
      multipleAccounts && site.seranking_email
        ? `${seRankingSiteLabel(site)} · ${site.seranking_email}`
        : seRankingSiteLabel(site),
  }));

  const canConnect = Boolean(siteId) && !isSaving;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose>
        <DialogHeader>
          <DialogTitle>{`Conectar ${seo.label}`}</DialogTitle>
        </DialogHeader>

        <DialogContent>
          <Stack spacing={2.5} sx={{ width: { xs: "100%", sm: 380 }, paddingTop: 1 }}>
            {client && <ConnectingClientField client={client} />}

            {error && (
              <Alert severity="error" sx={{ wordBreak: "break-word" }}>
                {error}
              </Alert>
            )}

            {userNotConnected && !error && (
              <AlertNote variant="warning">
                {seo.branded
                  ? "Tu usuario no está conectado a SE Ranking. Pídele a un superadministrador que vincule tu usuario con tu subcuenta de SE Ranking desde Configuración."
                  : SEO_PERMISSION_MESSAGE}
              </AlertNote>
            )}

            <Box>
              <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500 }}>
                Proyecto de {seo.label}:
              </Typography>
              <CustomSelector
                searchable
                value={siteId}
                onChange={setSiteId}
                options={siteOptions}
                disabled={isLoadingSites || !siteOptions.length}
                placeholder={
                  isLoadingSites
                    ? `Consultando ${seo.label}...`
                    : "Selecciona un proyecto"
                }
                leadingIcon={<Globe size={16} color={theme.palette.primary.main} />}
              />

              {!isLoadingSites &&
                !siteOptions.length &&
                !error &&
                !userNotConnected && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", marginTop: 0.5 }}
                  >
                    {seo.branded
                      ? "Tu subcuenta de SE Ranking no tiene proyectos disponibles."
                      : `No tienes proyectos de ${seo.label} disponibles. ${SEO_PERMISSION_MESSAGE}`}
                  </Typography>
                )}

              {currentLink?.id && (
                <Button
                  variant="ghost"
                  onClick={() => setConfirmUnlink(true)}
                  sx={{
                    paddingLeft: 0,
                    marginTop: 0.5,
                    color: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  <Unlink size={14} style={{ marginRight: 6 }} />
                  Desvincular de este cliente
                </Button>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          {onCreateProject && (
            <Button variant="ghost" onClick={onCreateProject} sx={{ marginRight: "auto" }}>
              <Plus size={14} style={{ marginRight: 6 }} />
              Crear proyecto nuevo
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            CANCELAR
          </Button>
          <Button onClick={handleConnect} disabled={!canConnect}>
            {isSaving ? <CircularProgress size={16} color="inherit" /> : "CONECTAR"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={confirmUnlink}
        onClose={() => setConfirmUnlink(false)}
        onConfirm={handleUnlink}
        title={`Desvincular ${seo.label}`}
        description={
          <>
            {`¿Estás seguro de que quieres desvincular el proyecto de ${seo.label} de este cliente? Se dejarán de mostrar su visibilidad, sus rankings y sus backlinks.`}
            {seo.branded && " Tu subcuenta de SE Ranking seguirá conectada a tu usuario."}
          </>
        }
        confirmLabel="Desvincular"
        loadingLabel={<CircularProgress size={16} color="inherit" />}
        loading={isUnlinking}
        tone="warning"
      />
    </>
  );
}

export default ConnectSeRankingModal;
